import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { consume } from '@lit/context';
import { CoursePlayerState, stateContext } from './course-player.context';
import '../../design-system/button/button';
import { i18n } from '../../i18n/i18n';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ANY_ROUTE, COMPLETE_ROUTE, DESCRIPTION_ROUTE, SCORM_CONTENT_ROUTE, SCORM_ROUTE } from './routes';

@customElement('lua-course-player-footer')
export class LuaCoursePlayerFooter extends LitElement {
  static override styles = [
    overrideDesignTokens,
    resetStyle,
    css`
      :host {
        --lua-course-player-footer-border-color: var(--strokes-stroke-secondary);
        --lua-course-player-footer-background-color: var(--surfaces-surface-light);
        --lua-course-player-footer-gap: var(--spacing-spacing-16);
        --lua-course-player-footer-padding: var(--spacing-spacing-12) var(--spacing-spacing-24);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        border-top: 1px solid var(--lua-course-player-footer-border-color);
        background-color: var(--lua-course-player-footer-background-color);
        gap: var(--lua-course-player-footer-gap);
        padding: var(--lua-course-player-footer-padding);
      }
    `,
  ];

  @consume({ context: stateContext, subscribe: true })
  private coursePlayerState: CoursePlayerState;

  private previousModuleHandler() {
    this.coursePlayerState.navigateToPreviousModule();
  }

  private nextModuleHandler() {
    this.coursePlayerState.navigateToNextModule();
  }

  private confirmCompletion() {
    this.coursePlayerState.confirmCompletion();
  }

  private isDescriptionPage(): boolean {
    const currentRoute = this.coursePlayerState.currentRoute;

    return currentRoute === DESCRIPTION_ROUTE;
  }

  override render() {
    const currentRoute = this.coursePlayerState.currentRoute;

    const ROUTES_WITH_FOOTER = [ANY_ROUTE, SCORM_ROUTE, SCORM_CONTENT_ROUTE, COMPLETE_ROUTE, DESCRIPTION_ROUTE];

    if (!ROUTES_WITH_FOOTER.includes(currentRoute)) {
      return nothing;
    }

    const showCompleteButton =
      this.coursePlayerState.flags.show_course_completion_button &&
      this.coursePlayerState.enrollment?.percentage_complete === 100;

    return html`
      ${this.coursePlayerState.isLoading
        ? nothing
        : html`
            <!-- Previous module -->
            ${this.coursePlayerState.hasPreviousModule()
              ? html`
                  <luds-button variant="secondary" data-qa-id="Previous module" @click=${this.previousModuleHandler}
                    >${i18n('lua.footer.prev_module')}</luds-button
                  >
                `
              : nothing}

            <!-- Next module -->
            ${this.coursePlayerState.hasNextModule()
              ? html`<luds-button
                  .variant="${showCompleteButton ? 'secondary' : 'primary'}"
                  @click=${this.nextModuleHandler}
                  data-qa-id="Next module"
                  >${i18n(this.isDescriptionPage() ? 'lua.footer.continue' : 'lua.footer.next_module')}</luds-button
                >`
              : nothing}

            <!-- Exit course -->
            ${this.coursePlayerState.showFooterExitCourseButton()
              ? html`<luds-button @click=${this.coursePlayerState.exitCourse} data-qa-id="Exit course"
                  >${i18n('lua.header.exit_course')}</luds-button
                >`
              : nothing}

            <!-- Complete course -->
            ${showCompleteButton
              ? html`<luds-button data-qa-id="Finish course" @click=${this.confirmCompletion}
                  >${i18n('lua.footer.complete_course')}</luds-button
                >`
              : nothing}
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-course-player-footer': LuaCoursePlayerFooter;
  }
}
